import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AmenityReturn, ReservationWaitlistReturn } from '../clients/rust-api-client';
import { memberReservationService } from '../clients/ApiClients';
import { Button } from '../Components/Catalyst/button';
import { Input } from '../Components/Catalyst/input';
import { Select } from '../Components/Catalyst/select';
import { Field, Label } from '../Components/Catalyst/fieldset';
import { Subheading } from '../Components/Catalyst/heading';
import { Text } from '../Components/Catalyst/text';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { useImpersonating } from '../Hooks/useImpersonating';
import { formatDuration, timesAreIn, todayIn } from './reservationFormat';
import { localFromInputs, waitlistWindowText } from './reservationPhase3';
import { reservationErrorMessage, ruleMessage, violationsFromError } from './ruleMessages';

interface Props {
  amenity: AmenityReturn;
  homeId: string;
}

const card =
  'rounded-2xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-gray-800';

/**
 * "Tell me if a time frees up" (#640 phase 3). The household names a day,
 * a window and how long it needs; when a booking in that window is
 * cancelled, the first household waiting is offered the freed time and
 * holds it for a while. The server refuses a window that has a free time
 * right now (`WAITLIST_SLOT_FREE` — book it instead) and one the rules
 * could never allow (422, worded like any booking refusal).
 */
const WaitlistJoinPanel: React.FC<Props> = ({ amenity, homeId }) => {
  const impersonating = useImpersonating();
  const durations: number[] = [];
  for (
    let m = amenity.min_duration_minutes;
    m <= amenity.max_duration_minutes && durations.length < 24;
    m += Math.max(amenity.slot_increment_minutes, 15)
  ) {
    durations.push(m);
  }
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayIn(amenity.timezone));
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('17:00');
  const [duration, setDuration] = useState<number>(durations[0] ?? amenity.min_duration_minutes);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joined, setJoined] = useState<ReservationWaitlistReturn | null>(null);

  const join = async (): Promise<void> => {
    const earliest = localFromInputs(date, from);
    const latest = localFromInputs(date, to);
    if (!earliest || !latest || latest <= earliest) {
      setError('Pick a day and a window whose end is after its start.');
      return;
    }
    setWorking(true);
    setError(null);
    try {
      const { data } = await memberReservationService.joinMyWaitlist({
        homeId,
        memberWaitlistJoin: {
          amenity_id: amenity.id,
          earliest_start_local: earliest,
          latest_end_local: latest,
          duration_minutes: duration,
        },
      });
      setJoined(data);
    } catch (err) {
      const refused = violationsFromError(err);
      setError(
        refused && refused.length
          ? refused.map((v) => ruleMessage(v.code, amenity)).join(' ')
          : reservationErrorMessage(err, "Couldn't add you to the waitlist.")
      );
    } finally {
      setWorking(false);
    }
  };

  if (joined) {
    return (
      <section className={card} aria-live="polite">
        <Subheading>You're on the waitlist</Subheading>
        <Text className="mt-2">{waitlistWindowText(joined)}</Text>
        <Text className="mt-2">
          {joined.position
            ? `You're number ${joined.position} in line. `
            : ''}
          If a time in that window frees up, we'll hold it for you and let you know.
        </Text>
        <Link
          to="/reservations#waitlist"
          className="mt-3 inline-block text-sm font-medium text-sky-700 hover:underline dark:text-sky-400"
        >
          See my waitlist
        </Link>
      </section>
    );
  }

  return (
    <section className={card}>
      <Subheading>Nothing free when you need it?</Subheading>
      {!open ? (
        <>
          <Text className="mt-2">
            Join the waitlist for a window, and we'll hold a time for you if one frees up.
          </Text>
          <Button outline className="mt-3 w-full sm:w-auto" onClick={() => setOpen(true)}>
            Join the waitlist
          </Button>
        </>
      ) : (
        <div className="mt-3 space-y-4">
          <Text className="text-sm">{timesAreIn(amenity.timezone)}</Text>
          <Field>
            <Label>Day</Label>
            <Input type="date" value={date} min={todayIn(amenity.timezone)} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label>From</Label>
              <Input type="time" step={900} value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field>
              <Label>Until</Label>
              <Input type="time" step={900} value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
          </div>
          {durations.length > 1 && (
            <Field>
              <Label>How long</Label>
              <Select value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))}>
                {durations.map((m) => (
                  <option key={m} value={m}>
                    {formatDuration(m)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {impersonating && (
            <Text className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
              This is a read-only view, so the household can't be added to the waitlist here.
            </Text>
          )}
          <ErrorAlert>{error}</ErrorAlert>
          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <Button outline onClick={() => setOpen(false)}>
              Never mind
            </Button>
            <Button color="sky" onClick={join} disabled={working || impersonating}>
              {working ? 'Joining…' : 'Join the waitlist'}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default WaitlistJoinPanel;
